import React, { useEffect, useRef } from 'react';

interface AgentChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onStop: () => void;
  isSending: boolean;
  disabled: boolean; // textarea + Send disabled (loading or mid-turn)
}

// Presentational composer. Ctrl/Cmd+Enter sends (mirrors GuidedConversation). While a turn
// is streaming, the Send button becomes a Stop button (Phase 3). The textarea auto-grows
// with its content (up to ~45vh) so long messages stay readable without manual dragging.
const AgentChatInput: React.FC<AgentChatInputProps> = ({ value, onChange, onSend, onStop, isSending, disabled }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto'; // shrink first so deletions reduce the height too
    const max = Math.round(window.innerHeight * 0.45);
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      if (!disabled && value.trim()) onSend();
    }
  };

  return (
    <div className="agent-chat-input">
      <textarea
        ref={textareaRef}
        className="agent-chat-textarea"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Describe your study, paste a draft, or ask for help… (Ctrl+Enter to send)"
        rows={4}
        disabled={disabled}
        aria-label="Message the assistant"
      />
      {isSending ? (
        <button className="agent-chat-stop" onClick={onStop} type="button" aria-label="Stop the assistant">
          Stop
        </button>
      ) : (
        <button className="agent-chat-send" onClick={onSend} disabled={disabled || !value.trim()}>
          Send
        </button>
      )}
    </div>
  );
};

export default AgentChatInput;
